import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  Send,
  X,
  Hash,
  Users,
  Plus,
  Search as SearchIcon,
  MoreVertical,
  ChevronLeft,
} from "lucide-react";
import { io } from "socket.io-client";
import { fetchAuth } from "../common/api";
import { formatLocalDate, formatLocalTime } from "../common/datetimeUtils";
import { API_BASE_URL, STORAGE_PREFIX } from "@config";


export default function ChatDock({ players = [], clubId, isDarkMode = false, currentUser }) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("channels");
  const [query, setQuery] = useState("");
  const [channels, setChannels] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [messages, setMessages] = useState({});
  const [typing, setTyping] = useState({});
  const scrollRef = useRef(null);
  const socketRef = useRef(null);
  const [meIdentity, setMeIdentity] = useState(null);
  const [editingChannel, setEditingChannel] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingMemberIds, setEditingMemberIds] = useState([]);
  const [hasMore, setHasMore] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    async function loadMeIdentity() {
      try {
        const res = await fetchAuth('/mobile/chat/me');
        if (!res.ok) return;
        const data = await res.json();
        setMeIdentity(data);
      } catch (e) {
        
      }
    }
    loadMeIdentity();
  }, []);

  const me = currentUser || {
    id: "staff-1",
    display_name: "Coach",
    role: "staff",
  };

  // Connexion socket (par ex seulement quand le dock est ouvert)
  useEffect(() => {
    if (!open) return;

    const token = localStorage.getItem(`${STORAGE_PREFIX}token`);
    const normalize = (u) => (u || "").trim().replace(/\/+$/, "");
    const WS_URL = normalize(API_BASE_URL).replace(/\/api$/, "");

    //localStorage.debug = "socket.io-client:*";

    const socket = io(WS_URL, {
      path: "/socket.io",
      auth: { token },
      transports: ["websocket", "polling"],
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("ping_test", { t: Date.now() });
    });



    socket.timeout(2000).emit("ping_test", { t: Date.now() }, (err, res) => {
    });

    socket.on("connect_error", (err) => {
   
    });

    const handler = (msg) => {

      setMessages((prev) => ({
        ...prev,
        [msg.channel_id]: [...(prev[msg.channel_id] || []), msg],
      }));
    };

    socket.on("chat:message:new", handler);

    return () => {
      socket.off("chat:message:new", handler);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [open]);


  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !activeChannelId) return;

    socket.emit("chat:join", { channel_id: activeChannelId });

    return () => {
      socket.emit("chat:leave", { channel_id: activeChannelId });
    };
  }, [activeChannelId, open]);

  const LS_PREFIX = `chat_${clubId || "club"}`;
  const ls = {
    get: (k, fallback) => {
      try {
        const raw = localStorage.getItem(`${LS_PREFIX}_${k}`);
        return raw ? JSON.parse(raw) : fallback;
      } catch {
        return fallback;
      }
    },
    set: (k, v) => localStorage.setItem(`${LS_PREFIX}_${k}`, JSON.stringify(v)),
  };

  const [panelWidth, setPanelWidth] = useState(() => {
    const w = ls.get("panelWidth", 420);
    return typeof w === "number" ? w : 420;
  });
  const resizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(420);

  function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

  useEffect(() => {
    ls.set("panelWidth", panelWidth);
  }, [panelWidth]);

  const loadChannels = React.useCallback(async () => {
    if (!clubId) return;
    try {
      const res = await fetchAuth(`/mobile/chat/channels`);
      if (!res.ok) return;
      const list = await res.json();
      setChannels(list);
      setActiveChannelId((prev) => {
        if (prev) return prev;
        return list.length > 0 ? list[0].id : null;
      });
    } catch (e) {
      console.error("Failed to load channels", e);
    }
  }, [clubId]);

  // Charger les channels au montage ET à chaque ouverture du panneau
  useEffect(() => {
    if (!clubId) return;
    loadChannels();
  }, [clubId, loadChannels]);

  useEffect(() => {
    if (open && clubId) loadChannels();
  }, [open]);

  useEffect(() => {
    if (activeChannelId) return;
    if (channels.length > 0) setActiveChannelId(channels[0].id);
  }, [channels, activeChannelId]);

  useEffect(() => {
    if (!activeChannelId) return;

    let cancelled = false;

    async function loadMessages() {
      try {
        const res = await fetchAuth(
          `/mobile/chat/channels/${activeChannelId}/messages?limit=50`
        );
        if (!res.ok) {
         
          return;
        }
        const data = await res.json();

        if (cancelled) return;

        setMessages((prev) => ({
          ...prev,
          [activeChannelId]: data.items,
        }));
        setHasMore((prev) => ({ ...prev, [activeChannelId]: data.has_more }));
      } catch (e) {
        if (!cancelled) {
          
        }
      }
    }

    loadMessages();

    return () => {
      cancelled = true;
    };
  }, [activeChannelId]);

  async function loadOlderMessages() {
    if (!activeChannelId || loadingMore) return;
    const current = messages[activeChannelId] || [];
    if (!current.length) return;

    const oldest = current[0];
    setLoadingMore(true);
    try {
      const res = await fetchAuth(
        `/mobile/chat/channels/${activeChannelId}/messages?limit=50&before=${oldest.created_at}`
      );
      if (!res.ok) return;
      const data = await res.json();
      setMessages((prev) => ({
        ...prev,
        [activeChannelId]: [...data.items, ...(prev[activeChannelId] || [])],
      }));
      setHasMore((prev) => ({ ...prev, [activeChannelId]: data.has_more }));
    } catch (e) {
      // ignore
    } finally {
      setLoadingMore(false);
    }
  }

  const theme = {
    panel: isDarkMode ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900",
    subpanel: isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200",
    input: isDarkMode ? "bg-gray-800 placeholder-gray-400 text-gray-100" : "bg-white placeholder-gray-500 text-gray-900",
    border: isDarkMode ? "border-gray-700" : "border-gray-200",
    hover: isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100",
    muted: isDarkMode ? "text-gray-400" : "text-gray-500",
    accent: "ring-2 ring-blue-500",
    chip: isDarkMode ? "bg-gray-700 text-gray-200" : "bg-gray-200 text-gray-800",
  };

  const filteredChannels = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q
      ? channels.filter((c) => c.name.toLowerCase().includes(q))
      : [...channels];
    list.sort((a, b) => (b.last_message_at || 0) - (a.last_message_at || 0));
    return list;
  }, [channels, query]);

  const directory = useMemo(() => {
    const base = players.map((p) => ({
      id: p.id,
      name: `${p.first_name || ""} ${p.last_name || ""}`.trim(),
      position: p.position || "",
    }));
    const q = query.trim().toLowerCase();
    return q ? base.filter((u) => u.name.toLowerCase().includes(q)) : base;
  }, [players, query]);

  const activeMessages = useMemo(() => messages[activeChannelId] || [], [messages, activeChannelId]);
  const activeChannel = useMemo(() => channels.find((c) => c.id === activeChannelId) || null, [channels, activeChannelId]);

  useEffect(() => {
    if (!open || !activeChannelId) return;
    scrollToBottom();
  }, [open, activeChannelId, activeMessages.length]);

  async function sendMessage(text) {
    const body = text?.trim();
    if (!body || !activeChannel) return;

    const res = await fetchAuth(
      `/mobile/chat/channels/${activeChannel.id}/messages`,
      {
        method: "POST",
        body: JSON.stringify({ body }),
      }
    );

    if (!res.ok) {
      
      return;
    }

    const msg = await res.json();

    // soit tu laisses uniquement le socket remplir le tableau
    // soit tu fais un optimistic update mais en évitant les doublons :
    setMessages((prev) => {
      const existing = prev[activeChannel.id] || [];
      if (existing.some((m) => m.id === msg.id)) return prev;
      return {
        ...prev,
        [activeChannel.id]: [...existing, msg],
      };
    });
  }

  function openEditChannel(channel) {
    if (!channel) return;

    setEditingChannel(channel);
    setEditingName(channel.name || "");

    // On essaie de reconstruire les membres à partir des infos dispo
    const initialMemberIds =
      channel.member_ids ||
      channel.members?.map(m => m.player_id || m.id) ||
      [];

    setEditingMemberIds(initialMemberIds);
  }

  async function saveChannelEdits() {
    if (!editingChannel) return;

    try {
      const res = await fetchAuth(
        `/mobile/staff/chat/channels/${editingChannel.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            name: editingName,
            player_ids: editingMemberIds,
          }),
        }
      );

      if (!res.ok) {
        
        return;
      }

      const updated = await res.json();

      // mettre à jour la liste locale
      setChannels(prev =>
        prev.map(c => (c.id === updated.id ? updated : c))
      );

      setEditingChannel(null);
    } catch (e) {
      
    }
  }

  async function createCustomChannel(name, memberIds) {
    try {
      const res = await fetchAuth(`/mobile/staff/chat/channels`, {
        method: "POST",
        body: JSON.stringify({
          name,
          member_ids: memberIds,  // me.id sera ajouté côté backend (tu le fais déjà)
          type: "custom",
        }),
      });

      if (!res.ok) {
       
        return;
      }

      const ch = await res.json(); // doit renvoyer le channel créé
      setChannels((prev) => [ch, ...prev]);
      setActiveChannelId(ch.id);
    } catch (e) {
    
    }
  }

  async function openDM(user) {
    try {
      const res = await fetchAuth(`/mobile/staff/chat/channels`, {
        method: "POST",
        body: JSON.stringify({
          name: user.name,
          member_ids: [user.id],
          type: "direct",
        }),
      });

      if (!res.ok) {
     
        return;
      }

      const ch = await res.json(); // channel direct renvoyé par l'API
      setChannels((prev) => {
        const exists = prev.some((c) => c.id === ch.id);
        return exists ? prev : [ch, ...prev];
      });
      setActiveChannelId(ch.id);
    } catch (e) {
   
    }
  }

  const onPointerMove = React.useCallback((e) => {
    if (!resizingRef.current) return;
    const delta = startXRef.current - e.clientX;
    const max = Math.min(900, (typeof window !== "undefined" ? window.innerWidth : 1200) - 80);
    const next = clamp(startWidthRef.current + delta, 320, max);
    setPanelWidth(next);
  }, []);

  const onPointerUp = React.useCallback(() => {
    if (!resizingRef.current) return;
    resizingRef.current = false;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  useEffect(() => {
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  return (
    <>
      {/* Fab bouton — masqué quand le panneau est ouvert */}
      {!open && (
        <motion.button
          className="fixed bottom-6 right-6 z-40 rounded-full shadow-xl px-5 py-4 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          whileTap={{ scale: 0.95 }}
          onClick={() => setOpen(true)}
          aria-label="Open chat"
        >
          <MessageCircle size={20} />
          <span className="hidden sm:inline">Messages</span>
        </motion.button>
      )}

      {/* Panneau latéral */}
      <AnimatePresence>
        {open && (
          <>
          {/* Overlay pour fermer en cliquant dehors */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-[45]"
            onClick={() => setOpen(false)}
          />
          <motion.aside
            initial={{ x: 420, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 420, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className={`fixed top-0 right-0 h-full w-full sm:border-l ${theme.panel} ${theme.border} z-50`}
            style={{ width: typeof window !== "undefined" && window.innerWidth >= 640 ? panelWidth : "100%" }}
          >
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize chat panel"
              onPointerDown={(e) => {
                if (typeof window !== "undefined" && window.innerWidth < 640) return;
                resizingRef.current = true;
                startXRef.current = e.clientX;
                startWidthRef.current = panelWidth;
                document.addEventListener("pointermove", onPointerMove);
                document.addEventListener("pointerup", onPointerUp);
              }}
              onDoubleClick={() => setPanelWidth((w) => (w < 520 ? 640 : 420))}
              className="hidden sm:block absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-blue-500/20 active:bg-blue-500/30"
              style={{ touchAction: "none" }}
            />
            {/* Header */}
            <div className={`flex items-center justify-between px-4 py-3 border-b ${theme.border}`}>
              <div className="flex items-center gap-2">
                <MessageCircle size={18} />
                <h3 className="font-semibold">Chat</h3>
              </div>
              <button onClick={() => setOpen(false)} className={`p-2 rounded-lg ${theme.hover}`} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Tabs + search */}
            <div className={`px-4 py-3 border-b ${theme.border} flex items-center gap-3`}> 
              <div className={`flex rounded-lg overflow-hidden border ${theme.border}`}>
                <button
                  onClick={() => setActiveTab("channels")}
                  className={`px-3 py-1 text-sm ${activeTab === "channels" ? "bg-blue-600 text-white" : theme.hover}`}
                >
                  Channels
                </button>
                <button
                  onClick={() => setActiveTab("directory")}
                  className={`px-3 py-1 text-sm ${activeTab === "directory" ? "bg-blue-600 text-white" : theme.hover}`}
                >
                  Directory
                </button>
              </div>
              <div className="relative flex-1">
                <input
                  className={`w-full pl-9 pr-3 py-2 rounded-lg border ${theme.input} ${theme.border} focus:outline-none focus:${theme.accent}`}
                  placeholder={activeTab === "channels" ? "Search channels…" : "Search players…"}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <SearchIcon size={16} className={`absolute left-3 top-2.5 ${theme.muted}`} />
              </div>
              {/* Créer un channel custom (staff uniquement) */}
              {me?.role === "staff" && activeTab === "channels" && (
                <button
                  title="New channel"
                  onClick={() => createCustomChannel(`Channel ${channels.length + 1}`, [])}
                  className={`p-2 rounded-lg border ${theme.border} ${theme.hover}`}
                >
                  <Plus size={16} />
                </button>
              )}
            </div>

            {/* Corps : split list + conversation */}
            <div className="h-[calc(100%-160px)] grid grid-cols-1 sm:grid-cols-5 min-h-0">
              <div className={`sm:col-span-2 border-r ${theme.border} overflow-y-auto min-h-0`}>
                {activeTab === "channels" ? (
                  <ChannelList
                    channels={filteredChannels}
                    activeId={activeChannelId}
                    onSelect={setActiveChannelId}
                    theme={theme}
                    canEdit={me?.role === "staff"}
                    onEditChannel={openEditChannel}
                  />
                ) : (
                  <DirectoryList
                    users={directory}
                    onOpenDM={openDM}
                    theme={theme}
                  />
                )}
              </div>

              {/* Conversation */}
              <div className="sm:col-span-3 flex flex-col min-h-0">
                {activeChannel ? (
                  <>
                    <ConversationHeader
                      channel={activeChannel}
                      theme={theme}
                      onClose={() => setActiveChannelId(null)}
                    />
                    <div
                      ref={scrollRef}
                      className={`flex-1 min-h-0 overflow-y-auto px-4 py-4 ${
                        isDarkMode ? "bg-gray-900" : "bg-white"
                      }`}
                    >
                      {hasMore[activeChannelId] && (
                        <div className="text-center mb-3">
                          <button
                            onClick={loadOlderMessages}
                            disabled={loadingMore}
                            className={`text-xs px-3 py-1 rounded-lg border ${theme.border} ${theme.hover} ${theme.muted} disabled:opacity-50`}
                          >
                            {loadingMore ? "Loading…" : "Load older messages"}
                          </button>
                        </div>
                      )}
                      <MessageList
                        messages={activeMessages}
                        meIdentityId={meIdentity?.id}
                        theme={theme}
                      />
                      {/* typing mock */}
                      {typing[activeChannel.id] && (
                        <div className={`text-xs ${theme.muted} mt-2`}>Someone is typing…</div>
                      )}
                    </div>
                    <Composer onSend={sendMessage} theme={theme} />
                  </>
                ) : (
                  <div className="flex-1 grid place-items-center text-sm opacity-70">Select a channel</div>
                )}
              </div>
            </div>
            {editingChannel && (
              <EditChannelModal
                theme={theme}
                channel={editingChannel}
                players={players}
                name={editingName}
                memberIds={editingMemberIds}
                setName={setEditingName}
                setMemberIds={setEditingMemberIds}
                onClose={() => setEditingChannel(null)}
                onSave={saveChannelEdits}
              />
            )}
          </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function ChannelList({ channels, activeId, onSelect, theme, canEdit, onEditChannel }) {
  if (!channels?.length) {
    return <div className={`p-4 text-sm ${theme.muted}`}>No channels yet.</div>;
  }
  return (
    <ul className="divide-y">
      {channels.map((c) => (
        <li key={c.id}>
          <button
            onClick={() => onSelect(c.id)}
            className={`w-full text-left px-4 py-3 flex items-center gap-3 ${
              activeId === c.id ? "bg-blue-50 dark:bg-blue-950/40" : ""
            } ${theme.hover}`}
          >
            {c.type === "club" ? (
              <Hash size={16} />
            ) : c.type === "direct" ? (
              <Users size={16} />
            ) : (
              <Hash size={16} />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{c.name}</div>
              <div className={`text-xs truncate ${theme.muted}`}>
                {c.type === "club"
                  ? "Club channel"
                  : c.type === "direct"
                  ? "Direct message"
                  : "Custom channel"}
              </div>
            </div>

            {/* Bouton édition pour le staff, sauf sur les DMs */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!canEdit) return;
                if (c.type === "direct") return;
                onEditChannel?.(c);
              }}
              className={`p-1 rounded-md hover:bg-black/5 dark:hover:bg-white/10`}
            >
              <MoreVertical size={16} className={theme.muted} />
            </button>
          </button>
        </li>
      ))}
    </ul>
  );
}

function DirectoryList({ users, onOpenDM, theme }) {
  return (
    <ul className="divide-y">
      {users.map((u) => (
        <li key={u.id}>
          <button
            onClick={() => onOpenDM(u)}
            className={`w-full text-left px-4 py-3 flex items-center gap-3 ${theme.hover}`}
          >
            <div className={`w-8 h-8 rounded-full grid place-items-center ${theme.chip}`}>{u.name[0] || "?"}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{u.name}</div>
              <div className={`text-xs truncate ${theme.muted}`}>{u.position || "Player"}</div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ConversationHeader({ channel, theme, onClose }) {
  return (
    <div className={`px-4 py-3 border-b ${theme.border} flex items-center gap-2`}> 
      {onClose && (
        <button
          onClick={onClose}
          className={`p-1.5 rounded-lg ${theme.hover}`}
          aria-label="Back to channels"
        >
          <ChevronLeft size={18} />
        </button>
      )}
      {channel.type === "direct" ? <Users size={16} /> : <Hash size={16} />}
      <div className="font-medium flex-1">{channel.name}</div>
      {onClose && (
        <button
          onClick={onClose}
          className={`p-1.5 rounded-lg ${theme.hover}`}
          aria-label="Close conversation"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}

function MessageList({ messages, meIdentityId, theme }) {
  const groups = useMemo(() => {
    const fmt = (t) => formatLocalDate(t);
    return messages.reduce((acc, m) => {
      const key = fmt(m.created_at);
      acc[key] = acc[key] || [];
      acc[key].push(m);
      return acc;
    }, {});
  }, [messages]);

  const orderedDays = Object.keys(groups).sort((a, b) => new Date(a) - new Date(b));

  return (
    <div className="space-y-6">
      {orderedDays.map((day) => (
        <div key={day}>
          <div className={`text-center text-xs ${theme.muted} mb-2`}>{day}</div>
          <div className="space-y-2">
            {groups[day].map((m) => (
              <MessageBubble
                key={m.id}
                mine={m.sender_identity_id === meIdentityId}
                message={m}
                theme={theme}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MessageBubble({ message, mine, theme }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-2 shadow-sm border ${
          mine ? "bg-blue-600 text-white border-blue-600" : `${theme.subpanel} ${theme.border}`
        }`}
      >
        {!mine && (
          <div className={`text-[11px] mb-0.5 ${theme.muted}`}>{message.sender_name || "Unknown"}</div>
        )}
        <div className="text-sm whitespace-pre-wrap break-words">{message.body}</div>
        <div className={`text-[10px] mt-1 ${mine ? "text-blue-100" : theme.muted}`}>
          {formatLocalTime(message.created_at)}
        </div>
      </div>
    </div>
  );
}

function Composer({ onSend, theme }) {
  const [value, setValue] = useState("");
  function submit() {
    if (!value.trim()) return;
    onSend(value);
    setValue("");
  }
  return (
    <div className={`p-3 border-t ${theme.border} flex items-center gap-2`}>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Write a message…"
        className={`flex-1 px-3 py-2 rounded-lg border ${theme.input} ${theme.border} focus:outline-none focus:${theme.accent}`}
      />
      <button
        onClick={submit}
        className={`px-3 py-2 rounded-lg flex items-center gap-2 ${
          "bg-blue-600 text-white hover:brightness-110"
        }`}
        aria-label="Send message"
      >
        <Send size={16} />
        <span className="hidden sm:inline text-sm">Send</span>
      </button>
    </div>
  );
}

function EditChannelModal({
  theme,
  channel,
  players,
  name,
  memberIds,
  setName,
  setMemberIds,
  onClose,
  onSave,
}) {
  const toggleMember = (id) => {
    setMemberIds((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* card */}
      <div
        className={`relative z-10 w-full max-w-lg rounded-2xl p-4 border ${theme.border} ${
          theme.panel
        } shadow-xl`}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Edit channel</h2>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg ${theme.hover}`}
          >
            <X size={16} />
          </button>
        </div>

        {/* Nom du channel */}
        <label className="block text-xs mb-1">Channel name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={`w-full mb-3 px-3 py-2 rounded-lg border ${theme.input} ${theme.border} focus:outline-none focus:${theme.accent}`}
        />

        {/* Participants */}
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium">Members</span>
          <span className={`text-[11px] ${theme.muted}`}>
            {memberIds.length} selected
          </span>
        </div>
        <div className="max-h-56 overflow-y-auto rounded-xl border p-2 mb-4 text-sm">
          {players.map((p) => {
            const id = p.id;
            const label = `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Unnamed";
            const checked = memberIds.includes(id);
            return (
              <label
                key={id}
                className="flex items-center justify-between px-2 py-1 rounded-lg cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleMember(id)}
                    className="h-3.5 w-3.5"
                  />
                  <span className="truncate">{label}</span>
                </div>
                {p.position && (
                  <span className={`text-[11px] ${theme.muted}`}>
                    {p.position}
                  </span>
                )}
              </label>
            );
          })}
          {!players.length && (
            <div className={`text-xs ${theme.muted} px-1 py-2`}>
              No players available to add.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={onClose}
            className={`px-3 py-1.5 rounded-lg text-sm border ${theme.border} ${theme.hover}`}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!name.trim()}
            className={`px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white hover:brightness-110 disabled:opacity-60`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}